import React, { useState, useRef } from 'react';
import { SystemMod } from '../types';
import { XIcon, PlusIcon, EditIcon, TrashIcon, ImageIcon, CameraIcon } from './Icons';
import { PfpCropperModal } from './PfpCropperModal';
import { uploadMediaFile } from '../utils/mediaUtils';

interface SystemModModalProps {
    isOpen: boolean;
    onClose: () => void;
    customMods: SystemMod[];
    activeSystemModId?: string;
    onSaveMod: (mod: SystemMod) => void;
    onDeleteMod: (modId: string) => void;
    onSelectMod: (modId: string) => void;
    currentUser: string;
}

export const SystemModModal = ({
    isOpen,
    onClose,
    customMods,
    activeSystemModId,
    onSaveMod,
    onDeleteMod,
    onSelectMod,
    currentUser
}: SystemModModalProps) => {
    console.log('[DEBUG-MODAL] customMods received:', customMods?.length);
    const [view, setView] = useState<'list' | 'form'>('list');
    const [editingMod, setEditingMod] = useState<SystemMod | null>(null);
    const [formData, setFormData] = useState({ name: '', icon: '', url: '', themeColor: '#000000', description: '' });

    // Cropping State
    const [cropImage, setCropImage] = useState<string | null>(null);
    const [isCropping, setIsCropping] = useState(false);
    const [isUploading, setIsUploading] = useState(false);
    const fileInputRef = useRef<HTMLInputElement>(null);

    if (!isOpen) return null;

    const handleCreateNew = () => {
        setEditingMod(null);
        setFormData({ name: '', icon: '', url: '', themeColor: '#000000', description: '' });
        setView('form');
    };

    const handleEdit = (mod: SystemMod) => {
        setEditingMod(mod);
        setFormData({ name: mod.name, icon: mod.icon, url: mod.url || '', themeColor: mod.themeColor || '#000000', description: mod.description || '' });
        setView('form');
    };
    
    const handleIconFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
            const reader = new FileReader();
            reader.onload = () => {
                setCropImage(reader.result as string);
                setIsCropping(true);
            };
            reader.readAsDataURL(file);
        }
    };

    const handleCropComplete = async (blob: Blob) => {
        setIsUploading(true);
        try {
            const fileToUpload = new File([blob], `mod-icon-${Date.now()}.png`, { type: 'image/png' });
            const url = await uploadMediaFile(fileToUpload, currentUser);
            setFormData(prev => ({ ...prev, icon: url }));
            setIsCropping(false);
            setCropImage(null);
        } catch (e) {
            console.error("Mod icon upload failed", e);
            alert("Failed to upload mod icon.");
        } finally {
            setIsUploading(false);
        }
    };

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        if (!formData.name.trim()) return;

        const rawUrl = formData.url.trim();
        const normalizedUrl = rawUrl
            ? (rawUrl.startsWith('http://') || rawUrl.startsWith('https://') ? rawUrl : `https://${rawUrl}`)
            : undefined;

        const mod: SystemMod = {
            id: editingMod?.id || `mod_${Date.now()}`,
            name: formData.name.trim(),
            icon: formData.icon.trim() || 'https://api.dicebear.com/7.x/bottts/svg?seed=mod&backgroundColor=b6e3f4',
            url: normalizedUrl,
            themeColor: formData.themeColor,
            description: formData.description.trim() || undefined
        };

        onSaveMod(mod);
        setView('list');
    };


    return (
        <div className="fixed inset-0 z-[110] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-fade-in">
            <div className="relative w-full max-w-md bg-[#161b22]/90 backdrop-blur-xl rounded-[2.5rem] border border-white/10 shadow-2xl overflow-hidden animate-scale-up">
                {/* Header */}
                <div className="flex items-center justify-between p-6 border-b border-white/5">
                    <div>
                        <h2 className="text-xl font-black text-white tracking-tight">
                            {view === 'list' ? 'System Mods' : (editingMod ? 'Edit Mod' : 'New Mod')}
                        </h2>
                        <p className="text-xs text-gray-400 font-medium">
                            {view === 'list' ? 'Customize your @system experience' : 'Define your custom mod identity'}
                        </p>
                    </div>
                    <button 
                        onClick={onClose}
                        className="p-2 rounded-full hover:bg-white/10 transition-colors"
                    >
                        <XIcon className="w-6 h-6 text-white/70" />
                    </button>
                </div>

                {/* Content */}
                <div className="p-6 max-h-[60vh] overflow-y-auto custom-scrollbar allow-scroll">
                    {view === 'list' ? (
                        <div className="grid grid-cols-1 gap-4">
                            {customMods.length === 0 ? (
                                <div className="text-center py-8">
                                    <div className="w-16 h-16 bg-white/5 rounded-2xl flex items-center justify-center mx-auto mb-4">
                                        <PlusIcon className="w-8 h-8 text-white/20" />
                                    </div>
                                    <p className="text-sm text-gray-500 font-medium italic">No custom mods yet.</p>
                                </div>
                            ) : (
                                customMods.map(mod => (
                                    <div
                                        key={mod.id}
                                        className={`group flex items-center gap-4 p-4 rounded-3xl border transition-all ${activeSystemModId === mod.id ? 'bg-blue-600/20 border-blue-500/50' : 'bg-white/5 hover:bg-white/10 border-white/5'}`}
                                    >
                                        <button
                                            onClick={() => {
                                                onSelectMod(mod.id);
                                                onClose();
                                            }}
                                            className="flex-1 flex items-center gap-4 text-left"
                                        >
                                            <div className="relative w-12 h-12 rounded-full overflow-hidden shadow-lg group-hover:scale-105 transition-transform bg-black/40">
                                                <img src={mod.icon} alt="" className="w-full h-full object-cover" />
                                            </div>
                                            <div className="flex-1">
                                                <h3 className="font-bold text-white text-sm">{mod.name}</h3>
                                                {activeSystemModId === mod.id && (
                                                    <span className="text-[10px] text-blue-400 font-black uppercase tracking-widest">Active</span>
                                                )}
                                            </div>
                                        </button>
                                        
                                        <div className="flex items-center gap-2">
                                            <button 
                                                onClick={() => handleEdit(mod)}
                                                className="p-2 rounded-xl bg-white/5 hover:bg-white/10 text-gray-400 hover:text-white transition-all"
                                            >
                                                <EditIcon className="w-4 h-4" />
                                            </button>
                                            <button 
                                                onClick={() => onDeleteMod(mod.id)}
                                                className="p-2 rounded-xl bg-red-500/10 hover:bg-red-500/20 text-red-400/70 hover:text-red-400 transition-all"
                                            >
                                                <TrashIcon className="w-4 h-4" />
                                            </button>
                                        </div>
                                    </div>
                                ))
                            )}

                            <button
                                onClick={handleCreateNew}
                                className="flex items-center justify-center gap-2 p-4 rounded-3xl border-2 border-dashed border-white/10 hover:border-blue-500/50 hover:bg-blue-500/5 text-gray-400 hover:text-blue-400 transition-all group"
                            >
                                <PlusIcon className="w-5 h-5 group-hover:rotate-90 transition-transform" />
                                <span className="font-bold text-sm uppercase tracking-wider">CREATE NEW MOD</span>
                            </button>
                        </div>
                    ) : (
                        <form onSubmit={handleSubmit} className="space-y-6">
                            <div className="space-y-2">
                                <label className="text-[10px] font-black text-gray-500 uppercase tracking-widest ml-1">Mod Name</label>
                                <input
                                    autoFocus
                                    type="text"
                                    value={formData.name}
                                    onChange={e => setFormData(prev => ({ ...prev, name: e.target.value }))}
                                    placeholder="Enter mod name..."
                                    className="w-full bg-white/5 border border-white/10 rounded-2xl p-4 text-white placeholder:text-gray-600 focus:outline-none focus:border-blue-500/50 transition-colors"
                                />
                            </div>

                            <div className="space-y-2">
                                <label className="text-[10px] font-black text-gray-500 uppercase tracking-widest ml-1">Source URL (Optional)</label>
                                <input
                                    type="text"
                                    value={formData.url}
                                    onChange={e => setFormData(prev => ({ ...prev, url: e.target.value }))}
                                    placeholder="https://example.com"
                                    className="w-full bg-white/5 border border-white/10 rounded-2xl p-4 text-white placeholder:text-gray-600 focus:outline-none focus:border-blue-500/50 transition-colors"
                                />
                            </div>

                            <div className="space-y-2">
                                <label className="text-[10px] font-black text-gray-500 uppercase tracking-widest ml-1">Description (Optional)</label>
                                <textarea
                                    value={formData.description}
                                    onChange={e => setFormData(prev => ({ ...prev, description: e.target.value }))}
                                    placeholder="Enter mod description to show in Pimp My Ride..."
                                    rows={3}
                                    className="w-full bg-white/5 border border-white/10 rounded-2xl p-4 text-white placeholder:text-gray-600 focus:outline-none focus:border-blue-500/50 transition-colors resize-none text-sm"
                                />
                            </div>

                            <div className="space-y-2">
                                <label className="text-[10px] font-black text-gray-500 uppercase tracking-widest ml-1">Badge Theme Color</label>
                                <div className="flex items-center gap-4 bg-white/5 border border-white/10 rounded-2xl p-4">
                                    <input
                                        type="color"
                                        value={formData.themeColor}
                                        onChange={e => setFormData(prev => ({ ...prev, themeColor: e.target.value }))}
                                        className="w-12 h-12 bg-transparent border-0 rounded cursor-pointer"
                                    />
                                    <span className="text-white text-xs font-mono uppercase">{formData.themeColor}</span>
                                </div>
                            </div>

                            <div className="space-y-2">
                                <label className="text-[10px] font-black text-gray-500 uppercase tracking-widest ml-1">Mod Icon</label>
                                <div className="flex flex-col items-center gap-4">
                                    <div 
                                        onClick={() => fileInputRef.current?.click()}
                                        className="relative w-32 h-32 rounded-full bg-black/40 border-2 border-dashed border-white/10 hover:border-blue-500/50 flex-shrink-0 overflow-hidden cursor-pointer group transition-all"
                                    >
                                        {formData.icon ? (
                                            <img src={formData.icon} alt="Preview" className="w-full h-full object-cover group-hover:opacity-50 transition-opacity" />
                                        ) : (
                                            <div className="w-full h-full flex flex-col items-center justify-center gap-2">
                                                <ImageIcon className="w-8 h-8 text-white/10" />
                                                <span className="text-[10px] text-white/20 font-bold">Tap to Upload</span>
                                            </div>
                                        )}
                                        <div className="absolute inset-0 bg-black/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                                            <CameraIcon className="w-8 h-8 text-white" />
                                        </div>
                                        {isUploading && (
                                            <div className="absolute inset-0 bg-black/60 flex items-center justify-center">
                                                <div className="w-6 h-6 border-2 border-white/20 border-t-white rounded-full animate-spin" />
                                            </div>
                                        )}
                                    </div>
                                    <input
                                        type="file"
                                        ref={fileInputRef}
                                        onChange={handleIconFileChange}
                                        accept="image/*"
                                        className="hidden"
                                    />
                                    <p className="text-[10px] text-gray-500 text-center max-w-[200px]">
                                        Authors can set a custom icon for their mod. This will be visible to all users.
                                    </p>
                                </div>
                            </div>

                            <div className="flex gap-3 pt-2">
                                <button
                                    type="button"
                                    onClick={() => setView('list')}
                                    className="flex-1 py-4 rounded-2xl bg-white/5 hover:bg-white/10 text-white font-bold transition-all"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    className="flex-[2] py-4 rounded-2xl bg-blue-600 hover:bg-blue-500 text-white font-bold shadow-lg shadow-blue-600/20 transition-all active:scale-[0.98]"
                                >
                                    {editingMod ? 'Update Mod' : 'Save Mod'}
                                </button>
                            </div>
                        </form>
                    )}
                </div>

                {isCropping && cropImage && (
                    <PfpCropperModal
                        image={cropImage}
                        onClose={() => { setIsCropping(false); setCropImage(null); }}
                        onCropComplete={handleCropComplete}
                    />
                )}

                {/* Footer Tip */}
                <div className="p-4 bg-white/5 text-center">
                    <p className="text-[9px] text-gray-500 uppercase tracking-widest font-black">
                        Tip: You can switch active mods at any time
                    </p>
                </div>
            </div>
        </div>
    );
};
